import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Paperclip, Upload, Download, Trash2, X, Eye, FileText, Image as ImageIcon, File as FileIcon } from 'lucide-react';
import { voucherAttachmentsApi, VoucherAttachment } from '../../services/api';
import { useToast } from '../Toast/Toast';

/**
 * Supporting documents on a transaction — supplier invoice, e-way bill, POD,
 * payment screenshot, etc.
 *
 * Works in two modes:
 *  - Saved voucher (vchId set): files upload immediately and are listed from
 *    the server.
 *  - New voucher (vchId null): there is no id to attach to yet, so files are
 *    STAGED in the parent's state and uploaded right after the voucher saves
 *    (see uploadStagedAttachments). Staged files can still be removed here.
 */

export const MAX_FILE_BYTES = 25 * 1024 * 1024;
export const MAX_PER_VOUCHER = 20;

export const fmtSize = (n: number): string => {
    if (!n) return '0 B';
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

/** Uploads the files staged while the voucher had no id yet. Returns how many
 *  went up; never throws so a failed attachment can't undo a saved voucher. */
export async function uploadStagedAttachments(vchId: number, files: File[]): Promise<number> {
    if (!vchId || !files?.length) return 0;
    try {
        const res = await voucherAttachmentsApi.upload(vchId, files);
        return res?.data?.saved ?? files.length;
    } catch {
        return 0;
    }
}

const iconFor = (name: string, mime: string | null) => {
    const m = (mime || '').toLowerCase();
    const ext = name.split('.').pop()?.toLowerCase() || '';
    if (m.startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp'].includes(ext)) {
        return <ImageIcon size={15} className="text-purple-500" />;
    }
    if (m === 'application/pdf' || ext === 'pdf') return <FileText size={15} className="text-red-500" />;
    if (['xls', 'xlsx', 'csv'].includes(ext)) return <FileText size={15} className="text-green-600" />;
    if (['doc', 'docx'].includes(ext)) return <FileText size={15} className="text-blue-600" />;
    return <FileIcon size={15} className="text-gray-400" />;
};

const canPreview = (name: string, mime: string | null) => {
    const m = (mime || '').toLowerCase();
    const ext = name.split('.').pop()?.toLowerCase() || '';
    return m.startsWith('image/') || m === 'application/pdf'
        || ['png', 'jpg', 'jpeg', 'gif', 'webp', 'pdf'].includes(ext);
};

interface Props {
    /** Voucher id once saved; null while creating a new voucher. */
    vchId: number | null;
    /** Voucher is locked (checked) — hide every mutating action. */
    readOnly?: boolean;
    /** User may attach / remove documents. */
    canEdit?: boolean;
    /** Files chosen before the voucher exists (create mode). Owned by the parent
     *  so it can upload them after save. */
    stagedFiles: File[];
    onStagedChange: (files: File[]) => void;
    /** Compact styling for the mobile toolbar. */
    compact?: boolean;
}

const VoucherAttachments: React.FC<Props> = ({
    vchId, readOnly = false, canEdit = true, stagedFiles, onStagedChange, compact = false,
}) => {
    const { showSuccess, showError } = useToast();
    const [open, setOpen] = useState(false);
    const [list, setList] = useState<VoucherAttachment[]>([]);
    const [loading, setLoading] = useState(false);
    const [uploadPct, setUploadPct] = useState<number | null>(null);
    const [dragOver, setDragOver] = useState(false);
    const [confirmDel, setConfirmDel] = useState<VoucherAttachment | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const editable = canEdit && !readOnly;
    const total = list.length + stagedFiles.length;

    const refresh = useCallback(async () => {
        if (!vchId) { setList([]); return; }
        setLoading(true);
        try {
            const res = await voucherAttachmentsApi.list(vchId);
            if (res.success) setList(res.data);
        } catch {
            /* leave the previous list in place — the modal shows the count anyway */
        }
        setLoading(false);
    }, [vchId]);

    // Keep the badge count right even while the modal is closed (edit mode).
    useEffect(() => { refresh(); }, [refresh]);

    const addFiles = async (picked: FileList | File[] | null) => {
        const files = Array.from(picked || []);
        if (!files.length) return;

        const tooBig = files.filter(f => f.size > MAX_FILE_BYTES);
        if (tooBig.length) {
            showError('File too large', `${tooBig.map(f => f.name).join(', ')} — limit is ${fmtSize(MAX_FILE_BYTES)} per file.`);
        }
        const ok = files.filter(f => f.size <= MAX_FILE_BYTES);
        if (!ok.length) return;
        if (total + ok.length > MAX_PER_VOUCHER) {
            showError('Too many documents', `A voucher can hold at most ${MAX_PER_VOUCHER} documents.`);
            return;
        }

        // No voucher id yet → stage locally, upload after the voucher saves.
        if (!vchId) {
            onStagedChange([...stagedFiles, ...ok]);
            return;
        }

        setUploadPct(0);
        try {
            await voucherAttachmentsApi.upload(vchId, ok, undefined, setUploadPct);
            showSuccess('Attached', `${ok.length} document(s) attached to this voucher.`);
            await refresh();
        } catch (err: any) {
            showError('Upload failed', err?.message || 'Could not attach the document.');
        }
        setUploadPct(null);
    };

    const doDelete = async (att: VoucherAttachment) => {
        setConfirmDel(null);
        try {
            await voucherAttachmentsApi.remove(att.id);
            showSuccess('Removed', `${att.original_name} was removed.`);
            await refresh();
        } catch (err: any) {
            showError('Remove failed', err?.message || 'Could not remove the document.');
        }
    };

    const doDownload = async (att: VoucherAttachment) => {
        try { await voucherAttachmentsApi.download(att.id, att.original_name); }
        catch (err: any) { showError('Download failed', err?.message || 'Could not download the document.'); }
    };

    const doPreview = async (att: VoucherAttachment) => {
        try { await voucherAttachmentsApi.preview(att.id); }
        catch (err: any) { showError('Preview failed', err?.message || 'Could not open the document.'); }
    };

    return (
        <>
            <button
                type="button"
                onClick={() => setOpen(true)}
                title="Attach supporting documents"
                className={compact
                    // Mobile wizard: full-width pill, same shape as the Print /
                    // Share buttons it sits beside.
                    ? 'w-full bg-slate-50 border border-slate-200 text-slate-700 text-sm font-semibold py-3 rounded-xl flex items-center justify-center gap-1.5'
                    : `relative inline-flex items-center gap-1 border rounded px-2.5 py-1 text-xs ${
                        total > 0
                            ? 'text-blue-700 border-blue-300 bg-blue-50 hover:bg-blue-100'
                            : 'text-gray-600 border-gray-300 hover:bg-gray-50 hover:text-gray-900'
                    }`}
            >
                <Paperclip size={compact ? 15 : 14} />
                {compact ? 'Attach Documents' : 'Attachments'}
                {total > 0 && (
                    <span className="ml-0.5 min-w-[18px] px-1 rounded-full bg-blue-600 text-white text-[10px] leading-[16px] text-center">
                        {total}
                    </span>
                )}
            </button>

            {open && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-3 z-50">
                    <div className="bg-white rounded-lg shadow-lg w-full max-w-lg max-h-[85vh] flex flex-col">
                        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                            <h2 className="text-base font-semibold text-gray-800 flex items-center gap-2">
                                <Paperclip size={16} /> Supporting Documents
                            </h2>
                            <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-600">
                                <X size={18} />
                            </button>
                        </div>

                        <div className="p-4 overflow-y-auto">
                            {editable && (
                                <div
                                    onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                                    onDragLeave={() => setDragOver(false)}
                                    onDrop={e => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
                                    onClick={() => inputRef.current?.click()}
                                    className={`border-2 border-dashed rounded p-4 text-center cursor-pointer transition-colors ${
                                        dragOver ? 'border-blue-400 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
                                    }`}
                                >
                                    <Upload size={20} className="mx-auto text-gray-400 mb-1" />
                                    <div className="text-sm text-gray-600">Drop files here, or click to browse</div>
                                    <div className="text-[11px] text-gray-400 mt-0.5">
                                        Up to {MAX_PER_VOUCHER} documents, {fmtSize(MAX_FILE_BYTES)} each
                                    </div>
                                    <input
                                        ref={inputRef} type="file" multiple className="hidden"
                                        onChange={e => { addFiles(e.target.files); e.target.value = ''; }}
                                    />
                                </div>
                            )}

                            {uploadPct !== null && (
                                <div className="mt-3 h-1.5 bg-gray-200 rounded overflow-hidden">
                                    <div className="h-full bg-blue-600 transition-all" style={{ width: `${uploadPct}%` }} />
                                </div>
                            )}

                            {!vchId && (
                                <div className="mt-3 text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                                    These files will be attached once you save the voucher.
                                </div>
                            )}

                            <div className="mt-3 divide-y divide-gray-100 border border-gray-200 rounded">
                                {loading && <div className="p-4 text-center text-sm text-gray-400">Loading...</div>}

                                {!loading && total === 0 && (
                                    <div className="p-6 text-center text-sm text-gray-400">No documents attached yet.</div>
                                )}

                                {/* Already uploaded */}
                                {list.map(att => (
                                    <div key={att.id} className="flex items-center gap-2 px-3 py-2">
                                        {iconFor(att.original_name, att.mime_type)}
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm text-gray-800 truncate" title={att.original_name}>
                                                {att.original_name}
                                            </div>
                                            <div className="text-[11px] text-gray-400">
                                                {fmtSize(att.size)}
                                                {att.uploaded_by ? ` · ${att.uploaded_by}` : ''}
                                                {att.created_at ? ` · ${new Date(att.created_at).toLocaleDateString('en-GB')}` : ''}
                                            </div>
                                        </div>
                                        {canPreview(att.original_name, att.mime_type) && (
                                            <button onClick={() => doPreview(att)} title="Preview"
                                                className="text-gray-400 hover:text-blue-600 p-1"><Eye size={15} /></button>
                                        )}
                                        <button onClick={() => doDownload(att)} title="Download"
                                            className="text-gray-400 hover:text-blue-600 p-1"><Download size={15} /></button>
                                        {editable && (
                                            <button onClick={() => setConfirmDel(att)} title="Remove"
                                                className="text-gray-400 hover:text-red-600 p-1"><Trash2 size={15} /></button>
                                        )}
                                    </div>
                                ))}

                                {/* Staged (voucher not saved yet) */}
                                {stagedFiles.map((f, i) => (
                                    <div key={`staged-${i}`} className="flex items-center gap-2 px-3 py-2 bg-amber-50/50">
                                        {iconFor(f.name, f.type)}
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm text-gray-800 truncate" title={f.name}>{f.name}</div>
                                            <div className="text-[11px] text-amber-600">{fmtSize(f.size)} · pending save</div>
                                        </div>
                                        {editable && (
                                            <button
                                                onClick={() => onStagedChange(stagedFiles.filter((_, idx) => idx !== i))}
                                                title="Remove" className="text-gray-400 hover:text-red-600 p-1"
                                            ><Trash2 size={15} /></button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="px-4 py-3 border-t border-gray-200 flex justify-end">
                            <button onClick={() => setOpen(false)}
                                className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50">Close</button>
                        </div>
                    </div>
                </div>
            )}

            {confirmDel && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-[60]">
                    <div className="bg-white rounded-lg shadow-lg w-full max-w-sm p-4">
                        <h3 className="text-base font-semibold text-gray-800">Remove document?</h3>
                        <p className="text-sm text-gray-600 mt-1 break-all">{confirmDel.original_name}</p>
                        <p className="text-xs text-gray-500 mt-1">The file is deleted from the server and cannot be recovered.</p>
                        <div className="flex justify-end gap-2 mt-4">
                            <button onClick={() => setConfirmDel(null)}
                                className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50">Cancel</button>
                            <button onClick={() => doDelete(confirmDel)}
                                className="px-4 py-2 text-sm bg-red-600 hover:bg-red-700 text-white rounded">Remove</button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default VoucherAttachments;
